import React, { useState } from 'react';
import { X, Mail, AlertCircle, RefreshCw } from 'lucide-react';
import { useHideNavOnModal } from '../hooks/useHideNavOnModal';

const EmailVerifyModal = ({ isOpen, tempToken, emailMasked, onSuccess, onCancel, api }) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState('');
  const [resendMessage, setResendMessage] = useState('');
  
  // Esconde nav quando modal está aberto
  useHideNavOnModal(isOpen);

  const handleVerify = async () => {
    if (code.length !== 6) {
      setError('Digite um código de 6 dígitos');
      return;
    }

    try {
      setLoading(true);
      setError('');
      console.log('Verifying email code...', { tempToken, code });
      const response = await api.post('/api/auth/email/verify', {
        temp_token: tempToken,
        code: code
      });
      console.log('Email verification response:', response.data);
      onSuccess(response.data);
      setCode('');
    } catch (error) {
      console.error('Email verification error:', error);
      setError(error.response?.data?.detail || 'Código inválido');
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      setResending(true);
      setError('');
      setResendMessage('');
      await api.post(`/api/auth/email/resend?temp_token=${tempToken}`);
      setResendMessage('Código reenviado com sucesso!');
      setTimeout(() => setResendMessage(''), 3000);
    } catch (error) {
      setError(error.response?.data?.detail || 'Erro ao reenviar código');
    } finally {
      setResending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && code.length === 6) {
      handleVerify();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50 backdrop-blur-sm modal-anim">
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl max-w-md w-full shadow-2xl overflow-hidden border-2 border-gray-700">
        {/* Header */}
        <div className="relative bg-gradient-to-br from-green-600 via-emerald-600 to-green-600 px-8 py-6">
          <div className="absolute inset-0 bg-gradient-to-br from-green-400/20 to-emerald-400/20"></div>
          <div className="relative text-center">
            <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <Mail size={32} className="text-white" strokeWidth={2.5} />
            </div>
            <h2 className="text-2xl font-black text-white mb-1">
              Verificação de Email
            </h2>
            <p className="text-green-100 text-sm font-medium">Digite o código enviado para seu email</p>
          </div>
        </div>

        <div className="p-8 space-y-6">
          {/* Email Info */}
          <div className="bg-green-900/30 border border-green-700 rounded-xl p-4 text-center">
            <p className="text-sm text-green-200">
              Código enviado para:
            </p>
            <p className="text-lg font-bold text-white mt-1">
              {emailMasked}
            </p>
          </div>

          {/* Code Input */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-bold text-gray-300 mb-3 text-center">
                Código de 6 dígitos
              </label>
              <input
                type="text"
                value={code}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setCode(value);
                  setError('');
                }}
                onKeyPress={handleKeyPress}
                placeholder="000000"
                className="w-full text-center text-3xl font-bold tracking-widest p-4 bg-gray-800 text-white border-2 border-gray-600 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                maxLength={6}
                autoFocus
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 text-red-400 text-sm font-semibold bg-red-900/30 px-4 py-3 rounded-lg border border-red-700">
                <AlertCircle size={16} />
                {error}
              </div>
            )}

            {resendMessage && (
              <div className="flex items-center gap-2 text-green-400 text-sm font-semibold bg-green-900/30 px-4 py-3 rounded-lg border border-green-700">
                {resendMessage}
              </div>
            )}
          </div>

          {/* Resend button */}
          <div className="text-center">
            <button
              onClick={handleResend}
              disabled={resending}
              className="text-blue-400 hover:text-blue-300 text-sm font-semibold flex items-center gap-2 mx-auto transition-colors disabled:opacity-50"
            >
              <RefreshCw size={16} className={resending ? 'animate-spin' : ''} />
              {resending ? 'Reenviando...' : 'Não recebeu? Reenviar código'}
            </button>
          </div>

          {/* Info */}
          <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-4">
            <p className="text-xs text-gray-400 text-center">
              O código é válido por 10 minutos e será enviado para o email cadastrado.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={onCancel}
              className="flex-1 px-6 py-4 bg-gray-700 text-gray-200 rounded-xl hover:bg-gray-600 transition-all font-bold"
            >
              Cancelar
            </button>
            <button
              onClick={handleVerify}
              disabled={loading || code.length !== 6}
              className="flex-1 px-6 py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all font-bold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              {loading ? 'Verificando...' : 'Verificar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerifyModal;
